import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  loginUser,
  logoutUser,
  registerUser,
  resetPassword,
  signInWithGoogle,
  signInAttendantWithGoogle,
  registerWithGoogle,
  initializeAuth,
} from "./authThunks";
import { AuthUser } from "@/types/auth.types";

interface AuthState {
  authUser: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

const initialState: AuthState = {
  authUser: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAuthUser: (state) => {
      state.authUser = null;
      state.isAuthenticated = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.authUser = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.authUser = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoading = false;
        state.isAuthenticated = false;
        state.authUser = null;
      });

    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.authUser = null;  
        state.error = null; 
        state.isAuthenticated = false;
      })
      .addCase(registerUser.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.authUser = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoading = false;
        state.authUser = null;  
        state.isAuthenticated = false;      
      });

    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isAuthenticated = false;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.authUser = null;
        state.isAuthenticated = false;
        state.isLoading = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoading = false;
        state.authUser = null;
        state.isAuthenticated = false;
      });

    builder
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoading = false;
      });

    builder
      .addCase(signInWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.authUser = null;
        state.isAuthenticated = false;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.authUser = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoading = false;
        state.isAuthenticated = false;
        state.authUser = null;
      });

    builder
      .addCase(signInAttendantWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.authUser = null;
        state.isAuthenticated = false;
      })
      .addCase(signInAttendantWithGoogle.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.authUser = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
      })
      .addCase(signInAttendantWithGoogle.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoading = false;
        state.isAuthenticated = false;
        state.authUser = null;
      });

    builder
      .addCase(registerWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.authUser = null;
        state.error = null;
      })
      .addCase(registerWithGoogle.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.authUser = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
      })
      .addCase(registerWithGoogle.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoading = false;
        state.authUser = null;
      });

    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
        state.isInitialized = false;
      })
      .addCase(initializeAuth.fulfilled, (state, action: PayloadAction<AuthUser | null>) => {
        if (action.payload) {
          state.authUser = action.payload;
          state.isAuthenticated = true;
        } else {
          state.authUser = null;
          state.isAuthenticated = false;
        }
        state.isLoading = false;
        state.isInitialized = true;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.authUser = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.isInitialized = true;
      });
  },
});

export const { setLoading, clearError, clearAuthUser } = authSlice.actions;
export default authSlice.reducer;
